import * as React from 'react';
import { v4 } from 'uuid';
import Styled, { css } from 'styled-components';

import { media } from '../../lib/StyledComponents';
import { cssDeclaration } from '../_ThemeTwo_/Utils/Styles/cssDeclaration';
import { calcBorderStyles } from '../_ThemeTwo_/Utils/Styles/calcBorderStyles';

import { IRadioProps, RadioContain } from './RadioCommon';
import { MultiRadioLabelStyles, RadioInputStyles, RadioLabelStyles } from './RadioInputCommon';

interface IRadioLabelProps extends React.AllHTMLAttributes<HTMLElement> {
  alert?: boolean;
  multiLine?: boolean;
}

export const RadioLabel = Styled.label<IRadioLabelProps>`
  ${({ multiLine }) => multiLine ? MultiRadioLabelStyles : RadioLabelStyles}

  ${({ theme: { radio, brand }, alert }) => alert && css`
    border: ${radio.borderWidth} solid ${brand.colorAlert};
    color: ${radio.colorTextAlert};

    &::before {
      width: ${({ theme: { radioInput } }) => radioInput.sizeRadioButtonHover};
      height: ${({ theme: { radioInput } }) => radioInput.sizeRadioButtonHover};
    }

    ${media.medium`
      &::before {
        border: ${radio.borderWidth} solid ${brand.colorAlert};
      }
    `}
  `}
`;

export const RadioInput = Styled.input<IRadioLabelProps>`
  ${RadioInputStyles}

  /* stylelint errors on RadioLabel */
  /* stylelint-disable selector-type-case, selector-type-no-unknown */
  &:hover + ${RadioLabel} {
    background: ${({ theme: { radio } }) => radio.colorBackgroundHover};
    cursor: pointer;

    &::before {
      border: ${({ theme: { radio, radioInput } }) => `${radio.borderWidth} solid ${radioInput.colorBorder}`};
      width: ${({ theme: { radioInput } }) => radioInput.sizeRadioButtonHover};
      height: ${({ theme: { radioInput } }) => radioInput.sizeRadioButtonHover};

      ${({ theme }) => theme.radioInput.hover && css`
        /* border */ ${theme.radioInput.hover.border && cssDeclaration(theme.themeTwo, 'border', theme.radioInput.hover.border)}
      `}
    }
  }

  &:checked:hover + ${RadioLabel}::before {
    width: ${({ theme: { radioInput } }) => radioInput.sizeRadioButton};
    height: ${({ theme: { radioInput } }) => radioInput.sizeRadioButton};
    background: ${({ theme: { radioInput } }) => radioInput.colorRadioButtonHover};
  }

  &:focus + ${RadioLabel} {
    outline: ${({ theme }) => theme.outline};
  }

  &:checked + ${RadioLabel} {
    color: ${({ multiLine, theme }) => multiLine ? theme.colorBlack : theme.radio.colorTextChecked};
    background: ${({ multiLine, theme }) => multiLine ? 'none' : theme.radio.colorBackgroundChecked};
    border: ${({ theme: { radioInput } }) => radioInput.borderWidth} solid ${({ theme: { radio } }) => radio.colorBorderChecked};
  }

  &:checked + ${RadioLabel}::before {
    background: ${({ theme: { radioInput } }) => radioInput.colorRadioLabelBackgroundActive};
    width: ${({ theme: { radioInput } }) => radioInput.sizeRadioButton};
    height: ${({ theme: { radioInput } }) => radioInput.sizeRadioButton};
  }

  &:checked + ${RadioLabel}::before {
    background: ${({ theme: { radioInput } }) => radioInput.colorRadioLabelBackgroundActive};
    width: ${({ theme: { radioInput } }) => radioInput.sizeRadioButton};
    height: ${({ theme: { radioInput } }) => radioInput.sizeRadioButton};
  }

  &:disabled + ${RadioLabel},
  &:disabled:checked + ${RadioLabel} {
    ${({ theme }) => theme.radioInput.disabled && css`
      cursor: not-allowed;
      color: ${theme.radioInput.disabled.labelColor && theme.radioInput.disabled.labelColor};

      &::before {
        background-color: ${theme.radioInput.disabled.colorBackground};
        /* border */ ${theme.radioInput.disabled.border && cssDeclaration(theme.themeTwo, 'border', theme.radioInput.disabled.border)};
      }
    `}
  }

  ${({ theme, alert }) => css`
    ${media.medium`
      ${alert && theme.radioInput.error && css`
        & + ${RadioLabel} {
          color: ${theme.radioInput.error && theme.radioInput.error.labelColor};
        }
        ${theme.radioInput.error.border && css`
          & + ${RadioLabel}::before {
            // can use cssDeclarationFunction inside media call
            /* border */ ${calcBorderStyles(theme.themeTwo, theme.radioInput.error.border)};
            width: ${theme.radioInput.sizeRadioButton};
            height: ${theme.radioInput.sizeRadioButton};
          }
       `}
      `}

      &:hover + ${RadioLabel} {
        background: none;
      }

      &:checked + ${RadioLabel} {
        color: ${theme.text.colorText};
        background: none;
        border: none;

        ${theme.radioInput.labelColorChecked && css`
          color: ${theme.radioInput.labelColorChecked};
        `}
      }

      /* Radio circle outline */
      &:checked + ${RadioLabel}::before {
        border: ${theme.radioInput.borderWidthActive} solid ${theme.radioInput.colorBorder};
        /* This is mainly for TD since their styling is drastically different. */
        ${theme.radioInput.hasActiveHeightAndWidth && `
          width: 0.625em !important;
          height: 0.625em !important;
        `}
      }

      /* Used for the inner mark */
      &:checked + ${RadioLabel}::after {
        position: absolute;
        content: '';
        border-radius: 50%;
        top: ${theme.radioInput.positionRadioButtonInnerMarkTop};
        left: ${theme.radioInput.positionRadioButtonInnerMarkLeft};
        width: ${theme.radioInput.sizeRadioButtonInnerMark};
        height: ${theme.radioInput.sizeRadioButtonInnerMark};
        background: ${theme.radioInput.colorInner};
      }

      &:checked:disabled + ${RadioLabel}::after {
        background: ${theme.radioInput.disabledChecked && theme.radioInput.disabledChecked.colorInner};
      }
    `}
  `}
  /* stylelint-enable */
`;

export const StyledRadioContain = Styled(RadioContain)`
  padding: 0.125em;
`;

export default class Radio extends React.PureComponent<IRadioProps> {
  private readonly _id: string = v4();

  public render () {
    const { children, id, inline, multiLine, ...restProps } = this.props;

    return (
      <StyledRadioContain>
        <RadioInput
          {...restProps}
          id={this._id}
          data-id={id}
          aria-checked={restProps.checked}
          type='radio'
          multiLine={multiLine}
        />
        <RadioLabel htmlFor={this._id} alert={restProps.alert} data-id={id} multiLine={multiLine}>{children}</RadioLabel>
      </StyledRadioContain>
    );
  }
}
